import { useGetAllAccounts } from '@/api/generated/accounts/accounts';
import { useGetAllDocuments } from '@/api/generated/documents/documents';
import { useGetAllGoals } from '@/api/generated/goals/goals';
import { useGetAllParties } from '@/api/generated/parties/parties';
import { useGetAllTransactions } from '@/api/generated/transactions/transactions';
import {
  searchService,
  type EnhancedSearchResponse,
  type SearchOptions,
} from '@/api/services/searchService';
import { localSearchEngine, type LocalSearchOptions, type SearchResult } from '@/utils/searchUtils';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseSearchResult {
  results: SearchResult[];
  isSearching: boolean;
  search: (options: LocalSearchOptions) => void;
  clearResults: () => void;
  hasResults: boolean;
  resultCount: number;
}

export interface UseGlobalSearchOptions {
  debounceMs?: number;
  minQueryLength?: number;
  enabled?: boolean;
}

export interface UseGlobalSearchResult {
  searchResults: EnhancedSearchResponse | null;
  isLoading: boolean;
  error: string | null;
  search: (query: string, options?: Partial<SearchOptions>) => void;
  clearResults: () => void;
}

/**
 * Hook for local search functionality
 * Automatically keeps search engine updated with latest data
 */
export function useLocalSearch(): UseSearchResult {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Fetch all data to keep search engine updated
  const { data: accountsResponse } = useGetAllAccounts();
  const { data: transactionsResponse } = useGetAllTransactions();
  const { data: goalsResponse } = useGetAllGoals();
  const { data: documentsResponse } = useGetAllDocuments();
  const { data: partiesResponse } = useGetAllParties();
  const accounts = accountsResponse?.items;
  const transactions = transactionsResponse?.items;
  const goals = goalsResponse?.items;
  const documents = documentsResponse?.items;
  const parties = partiesResponse?.items;

  // Update search engine when data changes
  useEffect(() => {
    if (accounts || transactions || goals || documents || parties) {
      localSearchEngine.updateData({
        accounts: accounts || [],
        transactions: transactions || [],
        goals: goals || [],
        documents: documents || [],
        parties: parties || [],
      });
    }
  }, [accounts, transactions, goals, documents, parties]);

  const search = useCallback((options: LocalSearchOptions) => {
    if (!options?.query?.trim()) {
      setResults([]);
      return;
    }

    setIsSearching(true);

    // Simulate slight delay for better UX
    const timeoutId = setTimeout(() => {
      try {
        const searchResults = localSearchEngine.search(options);
        setResults(searchResults);
      } catch (error) {
        console.error('Search failed:', error);
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 100);

    // Cleanup timeout if component unmounts or search changes
    return () => clearTimeout(timeoutId);
  }, []);

  const clearResults = useCallback(() => {
    setResults([]);
  }, []);

  return {
    results,
    isSearching,
    search,
    clearResults,
    hasResults: results.length > 0,
    resultCount: results.length,
  };
}

/**
 * Hook for quick search with debouncing
 */
export function useQuickSearch(query: string, debounceMs: number = 300): UseSearchResult {
  const searchHook = useLocalSearch();
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  // Debounce the query
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedQuery(query);
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [query, debounceMs]);

  // Perform search when debounced query changes
  useEffect(() => {
    if (debouncedQuery.trim()) {
      searchHook.search({
        query: debouncedQuery,
        limit: 10,
      });
    } else {
      searchHook.clearResults();
    }
  }, [debouncedQuery, searchHook]);

  return searchHook;
}

/**
 * Hook for global search command palette
 */
export function useSearchCommand() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchHook = useQuickSearch(query, 150);

  const openSearch = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    searchHook.clearResults();
  }, [searchHook]);

  const handleSearch = useCallback((newQuery: string) => {
    setQuery(newQuery);
  }, []);

  // Keyboard shortcut (Cmd+K or Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        openSearch();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [openSearch]);

  return {
    isOpen,
    query,
    openSearch,
    closeSearch,
    handleSearch,
    ...searchHook,
  };
}

/**
 * Hook for global search with backend integration and error handling
 */
export function useSearch(options: UseGlobalSearchOptions = {}): UseGlobalSearchResult {
  const { debounceMs = 300, minQueryLength = 2, enabled = true } = options;
  const [searchResults, setSearchResults] = useState<EnhancedSearchResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const search = useCallback(
    async (query: string, searchOptions?: Partial<SearchOptions>) => {
      // Clear previous error
      setError(null);

      // Validate query
      if (!query || !query.trim()) {
        setSearchResults(null);
        setIsLoading(false);
        return;
      }

      if (query.trim().length < minQueryLength) {
        setSearchResults(null);
        setIsLoading(false);
        return;
      }

      if (!enabled) {
        return;
      }

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Clear previous debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();
      const currentController = abortControllerRef.current;

      // Set loading state
      setIsLoading(true);

      // Debounce the search
      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          // Check if request was cancelled
          if (currentController.signal.aborted) {
            return;
          }

          const result = await searchService.search({
            query: query.trim(),
            limit: searchOptions?.limit || 20,
            types: searchOptions?.types,
            offset: searchOptions?.offset || 0,
          });

          // Check if request was cancelled after API call
          if (currentController.signal.aborted) {
            return;
          }

          setSearchResults(result);
          setError(null);
        } catch (err: unknown) {
          // Check if request was cancelled
          if (currentController.signal.aborted) {
            return;
          }

          console.error('Search error:', err);

          // Set user-friendly error message
          let errorMessage = 'Search failed. Please try again.';

          if (isAxiosError(err)) {
            if (err.response?.status === 429) {
              errorMessage = 'Too many requests. Please wait a moment and try again.';
            } else if (err.response?.status && err.response.status >= 500) {
              errorMessage = 'Server error. Please try again later.';
            } else if (err.response?.status === 404) {
              errorMessage = 'Search service unavailable.';
            }
          } else if (err instanceof Error) {
            if (err.message.includes('Network Error')) {
              errorMessage = 'Network error. Please check your connection.';
            }
          }

          setError(errorMessage);
          setSearchResults(null);
        } finally {
          // Only update loading state if request wasn't cancelled
          if (!currentController.signal.aborted) {
            setIsLoading(false);
          }
        }
      }, debounceMs);
    },
    [debounceMs, minQueryLength, enabled]
  );

  const clearResults = useCallback(() => {
    // Cancel any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Clear debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    setSearchResults(null);
    setError(null);
    setIsLoading(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return {
    searchResults,
    isLoading,
    error,
    search,
    clearResults,
  };
}
